import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { Box, Button, ButtonBase, Tooltip, Typography, useTheme } from "@mui/material";
import { MapOutlined, Today } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import type { BookingScheduleInterface } from "../../Booking-Form/typings/booking.types";
import { ScheduleCheckInUnlockActions } from "../../../components/Schedule/schedule-checkin-unlock-actions.component";
import type {
  Dashboard2DayBookingSummary,
  DayWorkStatus,
  DayWorkStatusKind
} from "../types/dashboard2-work-status.types";
import {
  extraBookingCount,
  statusAccentColor,
  statusPillPrimaryLine,
  statusSurfaceInkColors
} from "../utils/dashboard2-status-display.utils";
import { dashboard2TodayStripSx } from "../dashboard2.styles";
import { useElementWidth } from "../hooks/useElementWidth";
import { resolveTodayWideSideVisibility } from "../utils/dashboard2-today-wide-side.utils";
import {
  dashboard2TodayAdditionalBookingsLinkSx,
  dashboard2TodayStatusFieldsetSx,
  dashboard2TodayStatusHeadlineActionSx,
  dashboard2TodayStatusHeadlineBodySx,
  dashboard2TodayStatusHeadlineClickableSx,
  dashboard2TodayHeadlineActionButtonSx,
  dashboard2TodayHeadlineActionsContainerSx,
  dashboard2TodayStatusHeadlineSx,
  dashboard2TodayStatusIconSx,
  dashboard2FinishedStatusContentSx,
  dashboard2StatusFieldsetLegendIconSx,
  dashboard2StatusFieldsetLegendTitleSx,
  dashboard2TodayStatusHeadlineTextSx,
  dashboard2StatusDetailWrapSx,
  dashboard2StatusSurfaceSecondaryTextSx,
  dashboard2TodayMainColumnSx,
  dashboard2TodaySideBookingRowSx,
  dashboard2TodaySideBookingTextSx,
  dashboard2TodaySideBookingsSx,
  dashboard2TodaySideColumnSx,
  dashboard2TodaySideEmptySx,
  dashboard2TodaySideHintPointerSx,
  dashboard2TodaySideHintSx,
  dashboard2TodayWideContentSx
} from "../dashboard2-status.styles";
import { Dashboard2DayBookingsDialog } from "./Dashboard2DayBookingsDialog";
import { Dashboard2BookingBadgeChips } from "./Dashboard2BookingBadgeChips";
import { Dashboard2SectionPanel } from "./Dashboard2SectionPanel";
import { Dashboard2StatusIcon } from "./Dashboard2StatusIcon";
import { Dashboard2StatusInventoryTimeLines } from "./Dashboard2StatusInventoryTimeLines";
import { Dashboard2TodayWorkStatusCollapse } from "./Dashboard2TodayWorkStatusCollapse";
import { derivePrimaryCharacteristicBadge } from "../utils/dashboard2-primary-characteristic-badges.utils";
import { scheduleFloorPlanActionLabel } from "../utils/dashboard2-schedule.utils";
import { Dashboard2TodayStatusStripLoading } from "./Dashboard2SchedulesLoadingSkeleton";

type Dashboard2TodayStatusStripProps = {
  status: DayWorkStatus;
  primarySchedule?: BookingScheduleInterface;
  allSchedules?: BookingScheduleInterface[];
  refetchSchedules?: () => void;
  onClickUnlock?: () => void;
  isEditable?: boolean;
  isColleagueScheduleEditable?: boolean;
  onSelectStatus?: (kind: DayWorkStatusKind) => void;
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  onPrimaryScheduleClick?: (schedule: BookingScheduleInterface) => void;
  onOpenFloorPlan?: (schedule: BookingScheduleInterface) => void;
  mobileWorkingEnabled?: boolean;
  isLoading?: boolean;
  statusActionsDisabled?: boolean;
};

type Dashboard2TodaySideContentProps = {
  bookings: readonly Dashboard2DayBookingSummary[];
  onBookingClick?: (booking: Dashboard2DayBookingSummary) => void;
  title: string;
  emptyText: string;
  emptyPointerText: string;
};

const Dashboard2TodaySideContent: React.FC<Dashboard2TodaySideContentProps> = ({
  bookings,
  onBookingClick,
  title,
  emptyText,
  emptyPointerText
}) => {
  const theme = useTheme();
  const hasBookings = bookings.length > 0;

  if (!hasBookings) {
    return (
      <Box sx={dashboard2TodaySideEmptySx} data-testid="dashboard2-today-side-empty">
        <Typography variant="body2" sx={dashboard2TodaySideHintSx(theme)}>
          {emptyText}
        </Typography>
        <Typography
          variant="caption"
          sx={dashboard2TodaySideHintPointerSx(theme)}
          data-testid="dashboard2-today-side-hint-pointer"
        >
          {emptyPointerText}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={dashboard2TodaySideColumnSx} data-testid="dashboard2-today-side-bookings">
      <Typography variant="caption" fontWeight={600} color="text.secondary">
        {title}
      </Typography>
      <Box sx={dashboard2TodaySideBookingsSx}>
        {bookings.map(booking => (
          <Box
            key={booking.id}
            component={onBookingClick ? "button" : "div"}
            type={onBookingClick ? "button" : undefined}
            sx={dashboard2TodaySideBookingRowSx(Boolean(onBookingClick))}
            onClick={onBookingClick ? () => onBookingClick(booking) : undefined}
            data-testid={`dashboard2-today-side-booking-${booking.id}`}
          >
            <Typography variant="body2" fontWeight={600} sx={dashboard2TodaySideBookingTextSx}>
              {booking.primaryLine}
            </Typography>
            {booking.secondaryLine ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={dashboard2TodaySideBookingTextSx}
              >
                {booking.secondaryLine}
              </Typography>
            ) : null}
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={dashboard2TodaySideBookingTextSx}
            >
              {booking.typeLabel}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export const Dashboard2TodayStatusStrip: React.FC<Dashboard2TodayStatusStripProps> = ({
  status,
  primarySchedule,
  allSchedules,
  refetchSchedules,
  onClickUnlock,
  isEditable = true,
  isColleagueScheduleEditable = false,
  onSelectStatus,
  onBookingClick,
  onPrimaryScheduleClick,
  onOpenFloorPlan,
  mobileWorkingEnabled = true,
  isLoading = false,
  statusActionsDisabled = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [bodyMeasureRef, bodyWidth] = useElementWidth();
  const [sideVisible, setSideVisible] = useState(false);
  const todayLabel = DateTime.now().toFormat("ccc d LLL");
  const primaryLine = statusPillPrimaryLine(status, t);
  const accent = statusAccentColor(theme, status.kind);
  const surfaceInk = statusSurfaceInkColors(theme, status.kind);
  const additionalCount = extraBookingCount(status);
  const additionalBookings = status.additionalBookings ?? [];
  const hasListedAdditionalBookings = additionalBookings.length > 0;
  const { showInlineAdditionalBookings, showSideContent } = useMemo(
    () =>
      resolveTodayWideSideVisibility({
        contentWidth: bodyWidth,
        additionalCount,
        hasListedAdditionalBookings,
        sideCurrentlyVisible: sideVisible
      }),
    [additionalCount, bodyWidth, hasListedAdditionalBookings, sideVisible]
  );

  useLayoutEffect(() => {
    if (showSideContent !== sideVisible) {
      setSideVisible(showSideContent);
    }
  }, [showSideContent, sideVisible]);

  const showAdditionalBookingsLink = additionalCount > 0 && !showInlineAdditionalBookings;
  const showCheckInActions = Boolean(status.checkInAvailable && primarySchedule);
  const showFloorPlanAction =
    status.kind === "office" && Boolean(primarySchedule && onOpenFloorPlan);
  const showHeadlineActions = showFloorPlanAction || showCheckInActions;
  const [bookingsDialogOpen, setBookingsDialogOpen] = useState(false);
  const isPrimaryScheduleClickable = !!primarySchedule && !!onPrimaryScheduleClick;

  const handleOpenBookingsDialog = useCallback(() => {
    setBookingsDialogOpen(true);
  }, []);

  const handleCloseBookingsDialog = useCallback(() => {
    setBookingsDialogOpen(false);
  }, []);

  const handleOpenFloorPlan = useCallback(() => {
    if (primarySchedule) {
      onOpenFloorPlan?.(primarySchedule);
    }
  }, [onOpenFloorPlan, primarySchedule]);

  const handlePrimaryScheduleClick = useCallback(() => {
    if (primarySchedule) {
      onPrimaryScheduleClick?.(primarySchedule);
    }
  }, [onPrimaryScheduleClick, primarySchedule]);

  const floorPlanActionLabel = useMemo(() => {
    if (!primarySchedule) {
      return "";
    }

    return scheduleFloorPlanActionLabel(primarySchedule, t);
  }, [primarySchedule, t]);

  const primaryCharacteristicBadges = useMemo(() => {
    const showsPrimaryCharacteristics =
      primarySchedule && (status.kind === "office" || status.kind === "mobile_working");

    if (!showsPrimaryCharacteristics) {
      return [];
    }

    const badge = derivePrimaryCharacteristicBadge(primarySchedule, t);

    return badge ? [badge] : [];
  }, [primarySchedule, status.kind, t]);

  const headlineText = (
    <>
      <Typography
        variant="body1"
        fontWeight={600}
        sx={{
          ...(dashboard2StatusDetailWrapSx as object),
          color: surfaceInk.primary
        }}
      >
        {primaryLine}
      </Typography>
      <Dashboard2StatusInventoryTimeLines
        status={status}
        inventorySx={dashboard2StatusSurfaceSecondaryTextSx(theme, status.kind)}
        timeSx={dashboard2StatusSurfaceSecondaryTextSx(theme, status.kind)}
        t={t}
      />
    </>
  );

  const todayMainContent = (
    <>
      <Typography variant="body2" fontWeight={600} sx={{ color: surfaceInk.primary }}>
        {todayLabel}
      </Typography>

      <Box
        sx={{
          ...(dashboard2TodayStatusHeadlineSx as object),
          ...(status.isFinished ? (dashboard2FinishedStatusContentSx(theme) as object) : {})
        }}
      >
        <Box sx={dashboard2TodayStatusIconSx(theme, accent)}>
          <Dashboard2StatusIcon status={status} schedule={primarySchedule} iconSize={20} />
        </Box>
        <Box sx={dashboard2TodayStatusHeadlineBodySx}>
          {isPrimaryScheduleClickable ? (
            <ButtonBase
              onClick={handlePrimaryScheduleClick}
              sx={dashboard2TodayStatusHeadlineClickableSx(theme)}
              data-testid="dashboard2-today-primary-booking"
              aria-label={t("_dashboard2_viewBookingDetail")}
            >
              {headlineText}
            </ButtonBase>
          ) : (
            <Box sx={dashboard2TodayStatusHeadlineTextSx}>{headlineText}</Box>
          )}
          {showHeadlineActions && primarySchedule ? (
            <Box
              sx={{
                ...(dashboard2TodayStatusHeadlineActionSx as object),
                ...(dashboard2TodayHeadlineActionsContainerSx(theme) as object)
              }}
              data-testid="dashboard2-today-headline-actions"
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}
            >
              {showFloorPlanAction ? (
                <Tooltip title={floorPlanActionLabel}>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    size="medium"
                    aria-label={floorPlanActionLabel}
                    onClick={handleOpenFloorPlan}
                    data-testid="dashboard2-today-floor-plan-button"
                    sx={dashboard2TodayHeadlineActionButtonSx(theme)}
                  >
                    <MapOutlined fontSize="small" />
                  </Button>
                </Tooltip>
              ) : null}
              {showCheckInActions ? (
                <ScheduleCheckInUnlockActions
                  schedule={primarySchedule}
                  allSchedules={allSchedules}
                  refetchAllSchedule={refetchSchedules}
                  onClickUnlock={onClickUnlock}
                  isEditable={isEditable}
                  isColleagueScheduleEditable={isColleagueScheduleEditable}
                  layout="inline"
                  containerSx={dashboard2TodayHeadlineActionsContainerSx(theme)}
                  buttonSx={dashboard2TodayHeadlineActionButtonSx(theme)}
                />
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      {primaryCharacteristicBadges.length > 0 && (
        <Dashboard2BookingBadgeChips
          badges={primaryCharacteristicBadges}
          statusKind={status.kind}
          testId="dashboard2-today-booking-badges"
        />
      )}

      {showAdditionalBookingsLink ? (
        <Button
          variant="text"
          size="small"
          onClick={handleOpenBookingsDialog}
          aria-haspopup="dialog"
          aria-expanded={bookingsDialogOpen}
          sx={dashboard2TodayAdditionalBookingsLinkSx(theme, status.kind)}
          data-testid="dashboard2-today-view-additional-bookings"
        >
          {t("_dashboard2_statusSecondaryBookings", { count: additionalCount })}
        </Button>
      ) : null}
    </>
  );

  return (
    <Dashboard2SectionPanel
      icon={<Today fontSize="small" />}
      title={t("_dashboard2_todayFieldLabel")}
      description={t("_dashboard2_todayFieldDescription")}
      iconSx={dashboard2StatusFieldsetLegendIconSx(theme, status.kind)}
      titleSx={dashboard2StatusFieldsetLegendTitleSx(theme, status.kind)}
      panelSx={dashboard2TodayStatusFieldsetSx(theme, status.kind)}
      bodyRef={bodyMeasureRef}
      data-testid="dashboard2-today-status-strip"
      headerTestId="dashboard2-today-status-legend"
    >
      {isLoading ? (
        <Dashboard2TodayStatusStripLoading />
      ) : (
        <Box sx={dashboard2TodayStripSx} data-testid="dashboard2-today-strip-content">
          {showSideContent ? (
            <Box
              sx={dashboard2TodayWideContentSx(showInlineAdditionalBookings)}
              data-testid="dashboard2-today-wide-row"
              data-today-wide-layout={showInlineAdditionalBookings ? "bookings" : "hint"}
            >
              <Box sx={dashboard2TodayMainColumnSx}>{todayMainContent}</Box>
              <Dashboard2TodaySideContent
                bookings={additionalBookings}
                onBookingClick={onBookingClick}
                title={t("_dashboard2_additionalBookingsTitle")}
                emptyText={t("_dashboard2_needAnythingElseToday")}
                emptyPointerText={t("_dashboard2_todaySideHintQuickActionsPointer")}
              />
            </Box>
          ) : (
            todayMainContent
          )}

          <Dashboard2DayBookingsDialog
            open={bookingsDialogOpen}
            dayLabel={todayLabel}
            status={status}
            onClose={handleCloseBookingsDialog}
            onBookingClick={onBookingClick}
          />

          {onSelectStatus ? (
            <Dashboard2TodayWorkStatusCollapse
              status={status}
              onSelectStatus={onSelectStatus}
              onBookingClick={onBookingClick}
              primarySchedule={primarySchedule}
              onPrimaryScheduleClick={onPrimaryScheduleClick}
              mobileWorkingEnabled={mobileWorkingEnabled}
              disabled={statusActionsDisabled}
            />
          ) : null}
        </Box>
      )}
    </Dashboard2SectionPanel>
  );
};
